import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Calculator, ShieldAlert, TrendingDown, Flame } from "lucide-react";
import { COMPANY } from "@/data/company";

const SECTOR_OPTIONS = [
  { id: "ges", label: "Güneş Paneli (GES)", base: 0.7, avgLossPerM2: 1200 },
  { id: "uretim", label: "Üretim Tesisi", base: 1.0, avgLossPerM2: 2200 },
  { id: "depolama", label: "Depolama / Lojistik", base: 0.9, avgLossPerM2: 1800 },
  { id: "atik", label: "Atık Yönetimi", base: 1.15, avgLossPerM2: 1500 },
  { id: "tunel", label: "Otoyol / Tünel", base: 0.95, avgLossPerM2: 2500 },
  { id: "orman", label: "Orman / Saha", base: 0.85, avgLossPerM2: 900 },
  { id: "otel", label: "Otel / Konaklama", base: 0.8, avgLossPerM2: 2800 },
  { id: "sera", label: "Sera & Bitkisel Tarım", base: 0.6, avgLossPerM2: 1100 },
];

const EQUIPMENT_OPTIONS = [
  { id: "elektrik", label: "Elektrik Panoları & MCC", factor: 1.3 },
  { id: "motor", label: "Motor & Rulman", factor: 1.1 },
  { id: "kimyasal", label: "Kimyasal / Yanıcı Madde", factor: 1.4 },
  { id: "panel", label: "GES / Hücre Panelleri", factor: 1.0 },
  { id: "depo", label: "Depo Rafları", factor: 0.9 },
  { id: "soğutma", label: "Soğutma / HVAC", factor: 0.85 },
];

const RISK_PROFILES = {
  low: {
    label: "Düşük",
    color: "#10B981",
    range: [0, 30],
    desc: "Standart önlemler yeterli olabilir; yine de termal denetim önerilir.",
  },
  med: {
    label: "Orta",
    color: "#FACC15",
    range: [30, 60],
    desc: "Termal izleme ile %30-50 risk azaltımı önemli kazanç sağlar.",
  },
  high: {
    label: "Yüksek",
    color: "#FF5A00",
    range: [60, 80],
    desc: "Acil termal güvenlik denetimi ve sürekli izleme önerilir.",
  },
  critical: {
    label: "Kritik",
    color: "#E61919",
    range: [80, 101],
    desc: "Derhal aksiyon. Pilot proje ile 30 gün içinde önleyici sistem kurulmalı.",
  },
};

function getProfile(score) {
  if (score < 30) return RISK_PROFILES.low;
  if (score < 60) return RISK_PROFILES.med;
  if (score < 80) return RISK_PROFILES.high;
  return RISK_PROFILES.critical;
}

export default function RiskCalculator() {
  const [sector, setSector] = useState("uretim");
  const [equipment, setEquipment] = useState(["elektrik"]);
  const [area, setArea] = useState(2000);
  const [employees, setEmployees] = useState(50);
  const [hasMonitoring, setHasMonitoring] = useState(false);

  const result = useMemo(() => {
    const s = SECTOR_OPTIONS.find((x) => x.id === sector);
    const eqMax = equipment.length
      ? Math.max(
          ...equipment.map(
            (e) => EQUIPMENT_OPTIONS.find((x) => x.id === e)?.factor || 1
          )
        )
      : 0.8;

    // Risk score 0-100
    const sizeFactor = Math.min(1.4, 0.6 + area / 10000);
    const peopleFactor = Math.min(1.3, 0.7 + employees / 500);
    let score = 40 * s.base * eqMax * sizeFactor * peopleFactor;
    if (hasMonitoring) score *= 0.55;
    score = Math.max(5, Math.min(98, Math.round(score)));

    // Expected loss estimate (TL)
    const baseLoss = area * s.avgLossPerM2 * eqMax;
    const expectedLoss = Math.round(baseLoss);

    // Annual savings with thermal system
    const annualSavings = Math.round(expectedLoss * 0.78 * 0.1); // 78% preventable, 10% annual probability
    // Estimated downtime hours saved (70-85% reduction)
    const downtimeHoursSaved = Math.round((area / 100) * 12 * 0.77);
    // Deterministic life extension based on equipment severity (30-50%)
    const lifeExtensionPct = Math.round(30 + eqMax * 14);

    return {
      score,
      profile: getProfile(score),
      expectedLoss,
      annualSavings,
      downtimeHoursSaved,
      lifeExtensionPct,
    };
  }, [sector, equipment, area, employees, hasMonitoring]);

  const fmtTL = (n) =>
    new Intl.NumberFormat("tr-TR", {
      style: "currency",
      currency: "TRY",
      maximumFractionDigits: 0,
    }).format(n);

  const toggleEq = (id) => {
    setEquipment((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  return (
    <div data-testid="page-calculator" className="relative">
      <div className="absolute inset-x-0 top-0 h-[400px] bg-grid opacity-30 pointer-events-none" />
      <div
        className="absolute inset-x-0 top-0 h-[500px] pointer-events-none opacity-60"
        style={{
          background:
            "radial-gradient(ellipse at 50% 0%, rgba(255,90,0,0.22), transparent 60%)",
        }}
      />

      <section className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-16 pt-16 md:pt-24 pb-12">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-thermal-orange mb-4 flex items-center gap-2">
          <Calculator className="w-3.5 h-3.5" />
          / Risk Skoru Hesaplayıcı — Pre-Assessment
        </div>
        <h1 className="text-4xl md:text-6xl font-bold tracking-tight leading-[1.05] max-w-4xl">
          Tesisinizin termal risk skorunu <span className="thermal-text">30 saniyede</span> öğrenin.
        </h1>
        <p className="mt-6 max-w-2xl text-slate-300 text-lg leading-relaxed">
          Sektör + ekipman + alan büyüklüğüne göre tesisinizin yangın risk skorunu hesaplar,
          yıllık tasarruf potansiyelinizi gerçek 2025 istatistikleriyle çıkarırız.
        </p>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Inputs */}
          <div className="lg:col-span-7 terminal-border p-7 md:p-10">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-thermal-orange mb-6">
              / Input Parameters
            </div>

            {/* Sector */}
            <div className="mb-7">
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400 block mb-3">
                / 01 — Sektör
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {SECTOR_OPTIONS.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => setSector(s.id)}
                    data-testid={`calc-sector-${s.id}`}
                    className={`text-left px-3 py-2.5 text-xs border transition-all ${
                      sector === s.id
                        ? "bg-thermal-orange/10 border-thermal-orange text-thermal-orange"
                        : "border-white/15 text-slate-300 hover:border-white/30"
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Equipment */}
            <div className="mb-7">
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400 block mb-3">
                / 02 — Kritik Ekipman (çoklu seçim)
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {EQUIPMENT_OPTIONS.map((e) => (
                  <button
                    key={e.id}
                    onClick={() => toggleEq(e.id)}
                    data-testid={`calc-equipment-${e.id}`}
                    className={`text-left px-3 py-2.5 text-xs border transition-all ${
                      equipment.includes(e.id)
                        ? "bg-thermal-red/10 border-thermal-red text-thermal-yellow"
                        : "border-white/15 text-slate-300 hover:border-white/30"
                    }`}
                  >
                    {equipment.includes(e.id) && "▣ "}
                    {!equipment.includes(e.id) && "□ "}
                    {e.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Area */}
            <div className="mb-7">
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400 mb-3 flex items-center justify-between">
                <span>/ 03 — Tesis Alanı (m²)</span>
                <span className="font-mono text-thermal-yellow text-base">
                  {area.toLocaleString("tr-TR")} m²
                </span>
              </label>
              <input
                type="range"
                min="100"
                max="50000"
                step="100"
                value={area}
                onChange={(e) => setArea(parseInt(e.target.value, 10))}
                data-testid="calc-area"
                className="w-full accent-thermal-orange"
              />
              <div className="flex justify-between font-mono text-[10px] text-slate-500 mt-1">
                <span>100 m²</span>
                <span>50.000 m²</span>
              </div>
            </div>

            {/* Employees */}
            <div className="mb-7">
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400 mb-3 flex items-center justify-between">
                <span>/ 04 — Personel Sayısı</span>
                <span className="font-mono text-thermal-yellow text-base">
                  {employees} kişi
                </span>
              </label>
              <input
                type="range"
                min="1"
                max="1000"
                step="1"
                value={employees}
                onChange={(e) => setEmployees(parseInt(e.target.value, 10))}
                data-testid="calc-employees"
                className="w-full accent-thermal-orange"
              />
            </div>

            {/* Existing monitoring */}
            <div className="border border-white/10 p-4 flex items-start gap-3">
              <input
                id="hasMonitoring"
                type="checkbox"
                checked={hasMonitoring}
                onChange={(e) => setHasMonitoring(e.target.checked)}
                data-testid="calc-monitoring"
                className="mt-1 accent-thermal-orange"
              />
              <label htmlFor="hasMonitoring" className="text-sm text-slate-300 cursor-pointer">
                Mevcut yangın algılama / izleme sistemim var
                <div className="text-slate-500 text-xs mt-0.5">
                  Skor %45 azalır — yine de termal izleme katmanı eklemenizi öneririz.
                </div>
              </label>
            </div>
          </div>

          {/* Result */}
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-28 space-y-4">
              {/* Score */}
              <motion.div
                key={result.score + result.profile.label}
                initial={{ scale: 0.97, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.4 }}
                className="terminal-border p-7 relative overflow-hidden"
                data-testid="calc-result"
              >
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-2">
                  / Risk Skoru
                </div>
                <div className="flex items-end gap-3">
                  <div
                    className="font-mono font-bold leading-none tracking-tighter"
                    style={{ color: result.profile.color, fontSize: "5rem" }}
                  >
                    {result.score}
                  </div>
                  <div className="pb-3">
                    <div className="font-mono text-sm text-slate-400">/100</div>
                  </div>
                </div>

                <div className="mt-4 h-2 bg-white/5 border border-white/10 relative overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${result.score}%` }}
                    transition={{ duration: 0.6, ease: "easeOut" }}
                    style={{ background: result.profile.color }}
                    className="h-full"
                  />
                </div>

                <div
                  className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 border font-mono text-[10px] uppercase tracking-[0.2em]"
                  style={{
                    color: result.profile.color,
                    borderColor: result.profile.color + "55",
                  }}
                >
                  <ShieldAlert className="w-3.5 h-3.5" />
                  Seviye: {result.profile.label}
                </div>
                <p className="mt-3 text-sm text-slate-400 leading-relaxed">
                  {result.profile.desc}
                </p>
              </motion.div>

              {/* Projections */}
              <div className="terminal-border p-7">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-4">
                  / Termal Sistemle Projeksiyon
                </div>
                <div className="space-y-4">
                  <div className="flex items-start justify-between gap-4 pb-3 border-b border-white/10">
                    <div className="flex items-start gap-3">
                      <Flame className="w-4 h-4 text-thermal-red mt-0.5 shrink-0" />
                      <div>
                        <div className="text-sm text-slate-300">Beklenen yıllık hasar</div>
                        <div className="text-xs text-slate-500">Sektör ortalaması × alan</div>
                      </div>
                    </div>
                    <div
                      className="font-mono font-semibold text-thermal-red text-lg"
                      data-testid="calc-loss"
                    >
                      {fmtTL(result.expectedLoss)}
                    </div>
                  </div>

                  <div className="flex items-start justify-between gap-4 pb-3 border-b border-white/10">
                    <div className="flex items-start gap-3">
                      <TrendingDown className="w-4 h-4 text-thermal-green mt-0.5 shrink-0" />
                      <div>
                        <div className="text-sm text-slate-300">Tahmini yıllık tasarruf</div>
                        <div className="text-xs text-slate-500">%78 önlenebilirlik</div>
                      </div>
                    </div>
                    <div
                      className="font-mono font-semibold text-thermal-green text-lg"
                      data-testid="calc-saving"
                    >
                      {fmtTL(result.annualSavings)}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3 pt-1">
                    <div className="border border-white/10 p-3">
                      <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">
                        / Duruş Azalması
                      </div>
                      <div className="font-mono text-thermal-yellow text-xl mt-1">
                        {result.downtimeHoursSaved}h
                      </div>
                      <div className="text-[10px] text-slate-500">yıllık tahmini</div>
                    </div>
                    <div className="border border-white/10 p-3">
                      <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">
                        / Ekipman Ömrü
                      </div>
                      <div className="font-mono text-thermal-yellow text-xl mt-1">
                        +%{result.lifeExtensionPct}
                      </div>
                      <div className="text-[10px] text-slate-500">uzama</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* CTA */}
              <Link
                to="/iletisim"
                data-testid="calc-cta-contact"
                className="block w-full text-center bg-thermal-orange hover:bg-thermal-red text-white px-6 py-4 font-mono text-xs uppercase tracking-[0.2em] transition-colors"
              >
                <span className="inline-flex items-center gap-2">
                  Detaylı saha değerlendirmesi al <ArrowRight className="w-4 h-4" />
                </span>
              </Link>
              <p className="text-xs text-slate-500 text-center">
                Bu hesaplama yaklaşık projeksiyondur. Kesin değer için{" "}
                <a
                  href={`mailto:${COMPANY.email}`}
                  className="text-thermal-orange hover:underline"
                >
                  {COMPANY.email}
                </a>
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
